import { createFileRoute } from "@tanstack/react-router";
import {
  DashboardPageActions,
  DashboardPageHeader,
  DashboardPageShell,
} from "@/components/patterns/headers";
import { Button } from "@/components/ui/button";

import { useState } from "react";
import { toast } from "sonner";
import { UserAvatar } from "@/components/avatar";

import { ClockIcon, MailIcon, MoreHorizontalIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Loader2Icon } from "lucide-react";
import { InviteUserDialog } from "@/components/team/invite-user-dialog";
import SkeletonTeam from "@/components/skeleton-team";
import { useSession } from "@/hooks/use-session";
import { brandAccentAlpha } from "@/lib/brand-tokens";
import { useTRPC } from "@/lib/trpc";
import { toTitleCase } from "@/lib/utils";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_dashboard/team")({
  component: TeamPage,
});

const rolePillStyles: Record<string, string> = {
  Owner: "bg-brand-ink text-brand-accent",
  Admin: "bg-brand-ink/10 text-brand-ink",
  Member: "bg-brand-ink/[0.05] text-brand-ink/70",
};

function RolePill({ role }: { role: string }) {
  const label = toTitleCase(role);
  const styles = rolePillStyles[label] ?? "bg-brand-ink/[0.05] text-brand-ink/70";
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold tracking-widest uppercase",
        styles,
      )}
    >
      {label}
    </span>
  );
}

function MemberRow({
  member,
  canManageMembers,
  onDelete,
  onResend,
  onCopyLink,
  isPending: isMutating,
}: {
  member: {
    id?: string | null;
    fullName?: string | null;
    avatarUrl?: string | null;
    email?: string | null;
    appRole?: string | null;
    lastSignInAt?: string | Date | null;
    createdAt: string | Date;
  };
  canManageMembers: boolean;
  onDelete: (id: string) => void;
  onResend: (email: string, role: string, name: string) => void;
  onCopyLink: (email: string) => void;
  isPending: boolean;
}) {
  const isActive = !!member.lastSignInAt;

  return (
    <div
      className={cn(
        "group flex items-center gap-4 px-5 py-4 transition-colors duration-150",
        "border-b border-brand-ink/[0.06] hover:bg-brand-ink/[0.025] last:border-0",
      )}
    >
      <div className="relative shrink-0">
        <UserAvatar
          userId={member.id}
          name={member.fullName ?? ""}
          email={member.email}
          avatarUrl={member.avatarUrl}
          size="lg"
        />
        <span
          className={cn(
            "absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white",
            isActive ? "bg-brand-accent" : "bg-amber-400",
          )}
        />
      </div>

      {/* Name + email */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm leading-none font-semibold text-brand-ink">
            {member.fullName}
          </span>
          <RolePill role={member.appRole ?? "Member"} />
        </div>
        <div className="mt-1 flex items-center gap-1 text-xs text-brand-ink/45">
          <MailIcon className="h-3 w-3 shrink-0" />
          <span className="truncate">{member.email}</span>
        </div>
      </div>

      {/* Joined date */}
      <div className="hidden w-28 shrink-0 text-right text-xs tabular-nums text-brand-ink/40 sm:block">
        {new Date(member.createdAt).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}
      </div>

      {/* Actions */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="brandGhost"
            size="iconXs"
            className="opacity-30 transition-opacity duration-150 group-hover:opacity-100"
          >
            {isMutating ? (
              <Loader2Icon className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <MoreHorizontalIcon className="h-3.5 w-3.5" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[160px]">
          {!isActive && (
            <>
              <DropdownMenuItem
                onClick={() =>
                  onResend(member.email ?? "", member.appRole ?? "USER", member.fullName ?? "")
                }
                className="cursor-pointer text-sm"
              >
                Resend Invitation
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onCopyLink(member.email ?? "")}
                className="cursor-pointer text-sm"
              >
                Copy Invitation Link
              </DropdownMenuItem>
            </>
          )}
          {canManageMembers && (
            <DropdownMenuItem
              onClick={() => {
                if (member.id) {
                  onDelete(member.id);
                } else {
                  toast.error("Cannot delete user: Invalid user ID");
                }
              }}
              variant="destructive"
            >
              Offboard Member
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function TeamPage() {
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"ADMIN" | "USER">("USER");
  const { sessionData } = useSession();

  const trpc = useTRPC();
  const canManageMembers =
    sessionData?.app_role === "ADMIN" || sessionData?.app_role === "OWNER";

  const {
    data: { items: teamMembers = [] } = { items: [] },
    isLoading: isLoadingTeamMembers,
    isFetching: isFetchingTeamMembers,
  } = useQuery(trpc.userProfiles.getOrganizationUserProfiles.queryOptions({}));
  const queryClient = useQueryClient();

  const deleteUserMutation = useMutation(
    trpc.userProfiles.deleteUser.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.userProfiles.getOrganizationUserProfiles.queryKey({}),
        });
        toast.success("Member offboarded. Restore access from Activity History if needed.");
      },
    }),
  );

  const handleDeleteUser = async (userId: string) => {
    if (!canManageMembers) {
      toast.error("You don't have permission to offboard team members");
      return;
    }
    deleteUserMutation.mutate({ userId });
  };

  const generateInvitationLinkMutation = useMutation(
    trpc.userProfiles.generateInvitationLink.mutationOptions({
      onSuccess: async (data) => {
        await navigator.clipboard.writeText(data.invite_link);
        queryClient.invalidateQueries({
          queryKey: trpc.userProfiles.getOrganizationUserProfiles.queryKey({}),
        });
        toast.success("Invitation link copied to clipboard for " + data.email);
      },
    }),
  );

  const inviteUserMutation = useMutation(
    trpc.userProfiles.inviteUser.mutationOptions({
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: trpc.userProfiles.getOrganizationUserProfiles.queryKey({}),
        });
        toast.success("Invitation sent to " + data.email);
      },
    }),
  );

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await inviteUserMutation.mutateAsync({
        email: inviteEmail,
        appRole: inviteRole as "ADMIN" | "USER",
        fullName: inviteEmail.split("@")[0],
      });
      toast.success(`Invitation sent to ${inviteEmail} as ${inviteRole}.`);
      setInviteEmail("");
      setInviteRole("USER");
      setIsInviteDialogOpen(false);
    } catch (error) {
      console.error("Error inviting user:", error);
    }
  };

  const isMutating =
    isFetchingTeamMembers ||
    inviteUserMutation.isPending ||
    deleteUserMutation.isPending;

  const activeMembers = teamMembers.filter((m) => !!m.lastSignInAt);
  const pendingMembers = teamMembers.filter((m) => !m.lastSignInAt);

  if (isLoadingTeamMembers) {
    return <SkeletonTeam />;
  }

  return (
    <DashboardPageShell as="div" contentClassName="space-y-6">
      <DashboardPageHeader
        title="Team"
        titleTrailing={
          isMutating ? <Loader2Icon className="mt-0.5 h-5 w-5 animate-spin text-brand-ink/60" /> : null
        }
        description={
          <>
            <span className="font-semibold text-brand-ink">{teamMembers.length}</span>{" "}
            {teamMembers.length === 1 ? "member" : "members"}
            {activeMembers.length > 0 && (
              <>
                {" "}
                &middot;{" "}
                <span className="font-semibold text-brand-ink">{activeMembers.length}</span>{" "}
                active
              </>
            )}
            {pendingMembers.length > 0 && (
              <>
                {" "}
                &middot;{" "}
                <span className="font-semibold text-amber-600">
                  {pendingMembers.length}
                </span>{" "}
                pending
              </>
            )}
          </>
        }
        aside={
          <DashboardPageActions>
            <InviteUserDialog
              handleInviteSubmit={handleInviteSubmit}
              inviteRole={inviteRole}
              setInviteRole={setInviteRole}
              inviteEmail={inviteEmail}
              setInviteEmail={setInviteEmail}
              inviteUserMutation={inviteUserMutation}
              isInviteDialogOpen={isInviteDialogOpen}
              setIsInviteDialogOpen={setIsInviteDialogOpen}
            />
          </DashboardPageActions>
        }
      />

      {/* Roster */}
      <div className="overflow-hidden rounded-xl border border-brand-ink/12 shadow-sm">
        {/* Column headers */}
        <div className="flex items-center gap-4 border-b border-brand-ink/[0.08] bg-brand-ink/[0.05] px-5 py-2">
          {/* Avatar column placeholder */}
          <div className="w-9 shrink-0" />
          <span className="flex-1 text-[10px] font-bold uppercase tracking-[0.12em] text-brand-ink/40">
            Member
          </span>
          <span className="hidden w-28 shrink-0 text-right text-[10px] font-bold uppercase tracking-[0.12em] text-brand-ink/40 sm:block">
            Joined
          </span>
          {/* Action column placeholder */}
          <div className="w-7 shrink-0" />
        </div>

        {teamMembers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div
              className="h-12 w-12 rounded-full flex items-center justify-center mb-4"
              style={{ background: brandAccentAlpha(0.2) }}
            >
              <MailIcon className="h-5 w-5 text-brand-ink" />
            </div>
            <p className="text-sm font-medium text-brand-ink">No team members yet</p>
            <p className="mt-1 text-xs text-brand-ink/50">
              Invite colleagues to collaborate on your workspace. Archived members can be restored from Activity History.
            </p>
          </div>
        ) : (
          <>
            {activeMembers.map((member) => (
              <MemberRow
                key={member.id}
                member={member}
                canManageMembers={canManageMembers}
                onDelete={handleDeleteUser}
                onResend={(email, role, name) =>
                  inviteUserMutation.mutateAsync({
                    email,
                    appRole: role as "ADMIN" | "USER",
                    fullName: name,
                  })
                }
                onCopyLink={(email) =>
                  generateInvitationLinkMutation.mutateAsync({ email })
                }
                isPending={isMutating}
              />
            ))}

            {pendingMembers.length > 0 && (
              <>
                {activeMembers.length > 0 && (
                  <div
                    className="flex items-center gap-3 px-5 py-2"
                    style={{
                      background: "rgb(251 191 36 / 0.05)",
                      borderTop: `1px solid rgb(251 191 36 / 0.15)`,
                      borderBottom: `1px solid rgb(251 191 36 / 0.15)`,
                    }}
                  >
                    <ClockIcon className="h-3 w-3 text-amber-500 shrink-0" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-amber-600">
                      Awaiting acceptance
                    </span>
                  </div>
                )}
                {pendingMembers.map((member) => (
                  <MemberRow
                    key={member.id}
                    member={member}
                    canManageMembers={canManageMembers}
                    onDelete={handleDeleteUser}
                    onResend={(email, role, name) =>
                      inviteUserMutation.mutateAsync({
                        email,
                        appRole: role as "ADMIN" | "USER",
                        fullName: name,
                      })
                    }
                    onCopyLink={(email) =>
                      generateInvitationLinkMutation.mutateAsync({ email })
                    }
                    isPending={isMutating}
                  />
                ))}
              </>
            )}
          </>
        )}
      </div>
    </DashboardPageShell>
  );
}
